"use strict";
/**
 * 前端API客户端基类
 * 内部直接使用 @fegobull/webapi 的 useRequest，提供统一的API调用封装
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseApiClient = exports.ApiError = void 0;
exports.isApiError = isApiError;
exports.isApiServerError = isApiServerError;
exports.isApiBusinessError = isApiBusinessError;
exports.isApiNetworkError = isApiNetworkError;
exports.isApiRetryable = isApiRetryable;
const webapi_1 = require("@fegobull/webapi");
const site_location_1 = require("./site-location");
// ============ API 错误类 ============
/**
 * API 错误类
 *
 * 包含 HTTP 状态码和业务错误码，便于区分错误类型：
 * - 服务器错误（HTTP 5xx）：可重试
 * - 网络错误（超时、断网）：可重试
 * - 业务错误（HTTP 200, code !== 0）：需展示给用户
 */
class ApiError extends Error {
    constructor(options) {
        super(`API Error: HTTP ${options.httpStatus}, Code ${options.businessCode}`);
        this.name = 'ApiError';
        this.httpStatus = options.httpStatus;
        this.businessCode = options.businessCode;
        this.data = options.data;
        this.rawResponse = options.rawResponse;
    }
    /** 是否为服务器错误（HTTP 5xx） */
    get isServerError() {
        return this.httpStatus >= 500 && this.httpStatus < 600;
    }
    /** 是否为业务错误（HTTP 200, code !== 0） */
    get isBusinessError() {
        return this.httpStatus === 200 && this.businessCode !== 0;
    }
    /** 是否为网络错误（状态码为 0，通常是超时或断网） */
    get isNetworkError() {
        return this.httpStatus === 0;
    }
    /** 是否为可重试错误（服务器错误 或 网络错误） */
    get isRetryable() {
        return this.isServerError || this.isNetworkError;
    }
}
exports.ApiError = ApiError;
// ============ 类型守卫 ============
/** 判断是否为 ApiError */
function isApiError(e) {
    return e instanceof ApiError;
}
/** 判断是否为服务器错误（HTTP 5xx），可重试 */
function isApiServerError(e) {
    return isApiError(e) && e.isServerError;
}
/** 判断是否为业务错误（HTTP 200, code !== 0），需展示 */
function isApiBusinessError(e) {
    return isApiError(e) && e.isBusinessError;
}
/** 判断是否为网络错误（超时、断网等），可重试 */
function isApiNetworkError(e) {
    return isApiError(e) && e.isNetworkError;
}
/** 判断是否为可重试错误（服务器错误 或 网络错误） */
function isApiRetryable(e) {
    return isApiError(e) && e.isRetryable;
}
// ============ API 客户端基类 ============
/**
 * 前端API客户端基类
 *
 * 使用方式：
 * - 成功时直接返回 data（类型为 T）
 * - 失败时抛出 ApiError，包含 httpStatus、businessCode、data
 *
 * 错误处理示例：
 * ```typescript
 * try {
 *   const list = await api.getList();  // list: ListData
 * } catch (e) {
 *   if (isApiBusinessError(e)) {
 *     showToast(`业务错误: ${e.businessCode}`);
 *   }
 * }
 * ```
 *
 * 重试示例：
 * ```typescript
 * @Retry({ when: isApiServerError, maxAttempts: 3 })
 * async getList() { return this.get<ListData>('list'); }
 * ```
 */
class BaseApiClient {
    constructor(options = {}) {
        const { get, post } = webapi_1.use.useRequest();
        this.requestMethods = { get, post };
        this.baseURL = `${BaseApiClient.ARK_WEB_API_PREFIX}/${options.apiPrefix}`;
    }
    buildURL(path) {
        return `${this.baseURL}/${path}`;
    }
    /**
     * 获取所有请求默认携带的 headers
     * 包含站点地区标识，用于网关按地区转发
     */
    getDefaultHeaders() {
        const headers = {};
        const location = (0, site_location_1.getSiteLocation)();
        if (location) {
            headers[site_location_1.SITE_LOCATION_HEADER] = location;
        }
        return headers;
    }
    /**
     * 将默认 headers 合并到请求 options 中
     * 注意：Fego 的 xhr 封装使用 `header`（单数）作为 key
     */
    mergeOptions(options) {
        const defaultHeaders = this.getDefaultHeaders();
        if (Object.keys(defaultHeaders).length === 0)
            return options;
        return {
            ...options,
            header: { ...defaultHeaders, ...options?.header },
        };
    }
    /**
     * 安全解析 JSON 字符串
     * TODO ：： 抽离到 utils 库，或用开源库代替
     */
    safeParseJSON(text) {
        if (!text)
            return undefined;
        try {
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    /**
     * 将 useRequest 的错误标准化为 ApiError
     */
    normalizeError(error) {
        const xhr = error?.target;
        const httpStatus = xhr?.status ?? 0;
        // 解析响应数据，errorcode 在 data.code 中
        const data = this.safeParseJSON(xhr?.responseText);
        return new ApiError({
            httpStatus,
            businessCode: data?.code ?? -1,
            data,
            rawResponse: error,
        });
    }
    async get(path, params) {
        const url = this.buildURL(path);
        try {
            const data = await this.requestMethods.get(url, params, this.mergeOptions());
            return data;
        }
        catch (error) {
            throw this.normalizeError(error);
        }
    }
    async post(path, data) {
        const url = this.buildURL(path);
        try {
            const result = await this.requestMethods.post(url, data, this.mergeOptions());
            return result;
        }
        catch (error) {
            throw this.normalizeError(error);
        }
    }
}
exports.BaseApiClient = BaseApiClient;
BaseApiClient.ARK_WEB_API_PREFIX = '/apiv2/activity';
